// Workspace shell scope-tree API client (R-WIRE 2A/2B).
// The shell breadcrumb (workspace › client › engagement › project) sources its hierarchy
// from the backend scope_node tree (ADR-08), not per-domain tables. Cookie-based session —
// `credentials: "include"` sends the HttpOnly session cookie the `/scope` endpoints require
// (current_actor, fail-closed 401). Transport only: the node→FE-type mapping lives in
// ContextChain.tsx, next to the FE scope types it produces.

import { classifyFetchError } from "../state/denialErrors";
import { isLocalDevMockAuth } from "../../lib/auth/localDevMock";
import { invalidateScopedPages, queryClient, resetScopedPages } from "../../lib/queryClient";

const API_BASE = "/api/workspace/scope";

const FETCH_OPTS: RequestInit = {
  credentials: "include",
};

/** A scope_node as returned by GET/POST /scope/nodes — mirrors the BE NodeResponse. */
export interface ScopeNodeResp {
  id: string;
  node_type: "firm" | "workspace" | "client" | "engagement" | "project" | "app";
  parent_id: string | null;
  path: string;
  name: string;
  is_barrier: boolean;
  /** Client-only public research context. Optional keeps rolling deployments compatible with an
   *  older API response while the additive database migration is applied. */
  product_unit?: string | null;
  business_unit?: string | null;
  product_line?: string | null;
  region?: string | null;
  country?: string | null;
  customer_segment?: string | null;
  website_candidate?: string | null;
}

export interface CreateNodeInput {
  node_type: ScopeNodeResp["node_type"];
  name: string;
  parent_id?: string;
  /** Confidential (ethical wall). Omit ⇒ the BE type default (client defaults on, others off). An
   *  explicit false opts a client OUT — a client is default-confidential, NOT permanent (2026-07-03). */
  is_barrier?: boolean;
  /** Required by the backend when node_type is client; rejected for other node types. */
  product_unit?: string;
  business_unit?: string;
}

// Windows local development intentionally supports DB-less mock authentication. Keep the
// corresponding navigation fixture narrow: it contains only the lineage needed to review the
// Kenvue Nexus placement. The compile-time DEV + mock-auth gate below makes this unreachable in
// production/UAT bundles; all normal runtimes continue to use the wall-filtered backend tree.
const LOCAL_DEV_MOCK_SCOPE_NODES: readonly ScopeNodeResp[] = [
  {
    id: "firm-kearney",
    node_type: "firm",
    parent_id: null,
    path: "/firm-kearney/",
    name: "Kearney",
    is_barrier: false,
  },
  {
    id: "packaging",
    node_type: "workspace",
    parent_id: "firm-kearney",
    path: "/firm-kearney/packaging/",
    name: "Packaging",
    is_barrier: false,
  },
  {
    id: "kenvue",
    node_type: "client",
    parent_id: "packaging",
    path: "/firm-kearney/packaging/kenvue/",
    name: "Kenvue",
    is_barrier: true,
    product_unit: "Packaging",
    business_unit: "Consumer Health",
  },
  {
    id: "kenvue-cost-optimization",
    node_type: "engagement",
    parent_id: "kenvue",
    path: "/firm-kearney/packaging/kenvue/kenvue-cost-optimization/",
    name: "Cost Optimization",
    is_barrier: false,
  },
  {
    id: "kenvue-packaging-2026",
    node_type: "project",
    parent_id: "kenvue-cost-optimization",
    path: "/firm-kearney/packaging/kenvue/kenvue-cost-optimization/kenvue-packaging-2026/",
    name: "Kenvue Packaging Optimization — 2026",
    is_barrier: false,
  },
];

async function readErrorDetail(res: Response): Promise<string> {
  try {
    const data = (await res.json()) as { detail?: unknown };
    return typeof data.detail === "string" ? data.detail : JSON.stringify(data.detail ?? "");
  } catch {
    return "";
  }
}

/** Fetch the whole visible scope tree in one call (ordered by materialized path).
 *  Callers build the hierarchy from `parent_id`. */
export async function fetchScopeTree(): Promise<ScopeNodeResp[]> {
  if (isLocalDevMockAuth()) {
    // Return fresh objects so consumers cannot mutate the module-owned fixture across mounts.
    return LOCAL_DEV_MOCK_SCOPE_NODES.map((node) => ({ ...node }));
  }
  const res = await fetch(`${API_BASE}/nodes`, FETCH_OPTS);
  if (!res.ok) throw await classifyFetchError(res, "scope/nodes");
  return (await res.json()) as ScopeNodeResp[];
}

/** Create a scope node under `parent_id` (omit for a root). Returns the created node. */
export async function createScopeNode(input: CreateNodeInput): Promise<ScopeNodeResp> {
  const res = await fetch(`${API_BASE}/nodes`, {
    ...FETCH_OPTS,
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`create node failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  invalidateScopedPages();
  return (await res.json()) as ScopeNodeResp;
}

/** Rename a scope node (display name only). Returns the updated node. */
export async function renameScopeNode(id: string, name: string): Promise<ScopeNodeResp> {
  const res = await fetch(`${API_BASE}/nodes/${encodeURIComponent(id)}`, {
    ...FETCH_OPTS,
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ name }),
  });
  if (!res.ok) throw new Error(`rename failed: HTTP ${res.status} ${await readErrorDetail(res)}`);
  invalidateScopedPages();
  return (await res.json()) as ScopeNodeResp;
}

export interface ClientResearchContextPatch {
  product_unit?: string;
  business_unit?: string;
  product_line?: string | null;
  region?: string | null;
  country?: string | null;
  customer_segment?: string | null;
  website_candidate?: string | null;
}

/** Update only supplied dimensions. Omission preserves; explicit null clears optional hints. */
export async function updateClientResearchContext(
  id: string,
  context: ClientResearchContextPatch,
  fetchImpl: typeof fetch = fetch,
  apiBase = "/api/workspace",
): Promise<ScopeNodeResp> {
  const res = await fetchImpl(
    `${apiBase.replace(/\/$/, "")}/scope/nodes/${encodeURIComponent(id)}`,
    {
      ...FETCH_OPTS,
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(context),
    },
  );
  if (!res.ok) {
    throw await classifyFetchError(res, "update client research context");
  }
  // Invalidation alone retains old data while revalidating. Context edits may
  // change the company or geography, so those snapshots must disappear first.
  queryClient.removeQueries({ predicate: (query) => {
    if (query.queryKey[0] !== "scoped-page" || !["outside-in", "outside-in-about"].includes(String(query.queryKey[2]))) return false;
    const cached = query.state.data as { client_scope_node_id?: string | null; value?: { client_scope_node_id?: string | null } } | null | undefined;
    const cachedClient = cached?.client_scope_node_id ?? cached?.value?.client_scope_node_id;
    // Legacy cache entries without the client binding cannot safely be retained.
    return !cachedClient || cachedClient === id;
  } });
  invalidateScopedPages();
  return (await res.json()) as ScopeNodeResp;
}

/** Delete a leaf scope node. Workspace deletion may also purge its saved AI history. */
export async function deleteScopeNode(
  id: string,
  purgeWorkspaceData = false,
  fetchImpl: typeof fetch = fetch,
  apiBase = "/api/workspace",
): Promise<void> {
  const purgeQuery = purgeWorkspaceData ? "?purge_workspace_data=true" : "";
  const res = await fetchImpl(
    `${apiBase.replace(/\/$/, "")}/scope/nodes/${encodeURIComponent(id)}${purgeQuery}`,
    { ...FETCH_OPTS, method: "DELETE" },
  );
  if (!res.ok) throw await classifyFetchError(res, "delete scope node");
  resetScopedPages();
}
